/**
 * Rules for the image URLs admins paste onto services. Kept in one place so
 * the form, the save path and anything else that accepts a URL agree.
 */

export const normalizeImageUrl = (raw?: string | null): string => (raw ?? '').trim();

// Local Storage emulator origins (http) — only ever produced in emulator mode.
const EMULATOR_STORAGE_ORIGINS = ['http://127.0.0.1:9199', 'http://localhost:9199'];

/**
 * True when the URL points at a copy already stored in our own Storage by
 * the importServiceImage function — such links are saved as-is rather than
 * imported again.
 */
export const isStoredServiceImage = (raw?: string | null): boolean => {
  const url = normalizeImageUrl(raw);
  if (!url) return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  const fromOurStorage =
    (parsed.protocol === 'https:' && parsed.hostname === 'firebasestorage.googleapis.com') ||
    (process.env.NODE_ENV !== 'production' && EMULATOR_STORAGE_ORIGINS.includes(parsed.origin));
  return fromOurStorage && parsed.pathname.includes('/o/service_images%2F');
};

/**
 * Returns an error message, or null when the URL is acceptable. An empty
 * value is valid — the image is optional and clearing it removes it.
 */
export const validateImageUrl = (raw?: string | null): string | null => {
  const url = normalizeImageUrl(raw);
  if (!url) return null;

  // Our own emulator-stored copies are http by nature; they never exist in
  // a production build (isStoredServiceImage checks NODE_ENV).
  if (isStoredServiceImage(url)) return null;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return 'Enter a full link starting with https://';
  }

  // The mobile app blocks plain-http images on iOS (App Transport Security),
  // so an http link would show on Android and silently vanish on iPhones.
  if (parsed.protocol !== 'https:') {
    return 'Use an https:// link — http images are blocked on iPhones';
  }

  return null;
};
